import '../dashboard/Dashboard.css';
import { useState, useEffect } from 'react';
import { useDashboardData } from './useDashboard';
import userProfileStore from '../core/UserProfileStore';
import useUserSettingStore from './UserSettingStore';

export const VIS_DASHBOARD = {
  DASHBOARD_ID: 'vis-dashboard',
  DOMAIN_ID: 'vis',
  COST_PRICE_TREND_ID: 'cost-price-trend',
  APPRAISAL_INSIGHT_ID: 'appraisal-insight',
  LOOK_TO_BOOK_ID: 'look-to-book',
  USED_INV_AGING_ID: 'used-inventory-aging',
  NEW_INV_AGING_ID: 'new-inventory-aging',
  MDS_TREND_ID: 'mds-trend',
  APPRAISAL_PERCENTAGE_ID: 'appraisal-percentage',
};

export const getWidgets = (t: any) => {
  return [
    {
      id: VIS_DASHBOARD.COST_PRICE_TREND_ID,
      dashboardId: VIS_DASHBOARD.DASHBOARD_ID,
      title: t('dashboard.vis.costPriceTrend'),
      type: 'multiview',
      defaultView: 'bar',
      isPricing: true,
      isFilterAvailable: false,
      dataKey: ['Cost', 'Pricing'],
      colors: {
        Cost: '#2C4F81',
        Pricing: '#457CCF',
      },
      xKey: 'Age',
      thresholdFields: [{ name: 'Cost' }, { name: 'Pricing' }],
      availableViews: ['stacked-bar', 'bar', 'table', 'grid', 'line'],
      dataMapping: {
        transform: (sourceData: any) => {
          const rawData = sourceData[0]?.cost_pricing_trends || {};
          const chartData = [
            { Age: '<15', Cost: rawData.ctt_lt15, Pricing: rawData.ptt_lt15 },
            {
              Age: '15-30',
              Cost: rawData.ctt_15t30,
              Pricing: rawData.ptt_15t30,
            },
            {
              Age: '31-45',
              Cost: rawData.ctt_31t45,
              Pricing: rawData.ptt_31t45,
            },
            {
              Age: '46-60',
              Cost: rawData.ctt_46t60,
              Pricing: rawData.ptt_46t60,
            },
            {
              Age: '61-90',
              Cost: rawData.ctt_61t90,
              Pricing: rawData.ptt_61t90,
            },
            { Age: '>90', Cost: rawData.ctt_gt90, Pricing: rawData.ptt_gt90 },
          ];
          return chartData;
        },
      },
    },
    {
      id: VIS_DASHBOARD.APPRAISAL_INSIGHT_ID,
      dashboardId: VIS_DASHBOARD.DASHBOARD_ID,
      isFilterAvailable: false,
      type: 'multiview',
      size: 'small',
      title: t('dashboard.vis.appraisalInsight'),
      defaultView: 'funnel',
      xKey: 'name',
      valueKey: 'value',
      availableViews: ['funnel', 'bar', 'table', 'grid'],
      thresholdFields: [
        { name: 'Created' },
        { name: 'Completed' },
        { name: 'Won' },
      ],
      dataMapping: {
        transform: (sourceData: any) => {
          const rawData = sourceData[0]?.appraisal_insights || {};
          const data = [
            { value: rawData.appraisals_created, name: 'Created' },
            { value: rawData.appraisals_completed, name: 'Completed' },
            { value: rawData.appraisals_won, name: 'Won' },
          ];
          return data;
        },
      },
    },
    {
      id: VIS_DASHBOARD.APPRAISAL_PERCENTAGE_ID,
      dashboardId: VIS_DASHBOARD.DASHBOARD_ID,
      isFilterAvailable: false,
      type: 'metric',
      size: 'small',
      title: t('dashboard.vis.appraisalPercentage'),
      defaultView: 'metrics',
      availableViews: ['metrics'],
      thresholdFields: [{ name: 'Appraisal Completion Percentage' }],
      dataMapping: {
        transform: (sourceData: any) => {
          const rawData = sourceData[0]?.appraisal_insights || {};
          return {
            name: 'Appraisal Completion Percentage',
            count: rawData.appraisals_completion_rate || 0,
            label: `${rawData.appraisals_completion_rate || 0}%`,
          };
        },
      },
    },
    {
      id: VIS_DASHBOARD.LOOK_TO_BOOK_ID,
      dashboardId: VIS_DASHBOARD.DASHBOARD_ID,
      isFilterAvailable: false,
      type: 'metric',
      size: 'small',
      title: t('dashboard.vis.lookToBook'),
      defaultView: 'metrics',
      availableViews: ['metrics'],
      thresholdFields: [{ name: 'Look to Book Percentage' }],
      dataMapping: {
        transform: (sourceData: any) => {
          const rawData = sourceData[0]?.appraisal_insights || {};
          return {
            name: 'Look to Book Percentage',
            count: rawData.look_to_book_rate || 0,
            label: `${rawData.look_to_book_rate || 0}%`,
          };
        },
      },
    },
    {
      id: VIS_DASHBOARD.USED_INV_AGING_ID,
      dashboardId: VIS_DASHBOARD.DASHBOARD_ID,
      isFilterAvailable: false,
      title: t('dashboard.vis.usedInventoryAging'),
      type: 'multiview',
      defaultView: 'bar',
      valueKey: 'Count',
      xKey: 'Age',
      thresholdFields: [{ name: 'Count' }],
      availableViews: ['bar', 'table', 'grid'],
      dataMapping: {
        transform: (sourceData: any) => {
          const rawData = sourceData[0]?.inventory_aging?.used || {};
          const chartData = [
            { Age: '<15', Count: rawData['< 15'] },
            { Age: '15-30', Count: rawData['15-30'] },
            { Age: '31-45', Count: rawData['31-45'] },
            { Age: '46-60', Count: rawData['46-60'] },
            { Age: '61-90', Count: rawData['61-90'] },
            { Age: '>90', Count: rawData['> 90'] },
          ];
          return chartData;
        },
      },
    },
    {
      id: VIS_DASHBOARD.NEW_INV_AGING_ID,
      dashboardId: VIS_DASHBOARD.DASHBOARD_ID,
      isFilterAvailable: false,
      title: t('dashboard.vis.newInventoryAging'),
      type: 'multiview',
      defaultView: 'bar',
      valueKey: 'Count',
      xKey: 'Age',
      thresholdFields: [{ name: 'Count' }],
      availableViews: ['bar', 'table', 'grid'],
      dataMapping: {
        transform: (sourceData: any) => {
          const rawData = sourceData[0]?.inventory_aging?.new || {};
          const chartData = [
            { Age: '<15', Count: rawData['< 15'] },
            { Age: '15-30', Count: rawData['15-30'] },
            { Age: '31-45', Count: rawData['31-45'] },
            { Age: '46-60', Count: rawData['46-60'] },
            { Age: '61-90', Count: rawData['61-90'] },
            { Age: '>90', Count: rawData['> 90'] },
          ];
          return chartData;
        },
      },
    },
    {
      id: VIS_DASHBOARD.MDS_TREND_ID,
      dashboardId: VIS_DASHBOARD.DASHBOARD_ID,
      isFilterAvailable: false,
      title: t('dashboard.vis.mdsTrend'),
      type: 'multiview',
      defaultView: 'line',
      xKey: 'Date',
      valueKey: 'MDS',
      thresholdFields: [{ name: 'MDS' }],
      availableViews: ['line', 'bar', 'table', 'grid'],
      dataMapping: {
        transform: (sourceData: any) => {
          const rawData = sourceData[0]?.mds_trends || [];
          return rawData.length > 0
            ? rawData.map((item: any) => ({
                Date: item.date,
                MDS: item.avg_mds,
              }))
            : [];
        },
      },
    },
  ];
};

export const getDashboardConfig = (storeId: string) => {
  const config = [];
  config.push({
    id: 'vis-metrics-query',
    apiConfig: {
      method: 'GET',
      endpoint: `/api/getVISMetrics/${storeId}`,
    },
  });
  return config;
};

export const useVISDashboard = ({ enabled = true, filteredWidgets = [] }: { enabled?: boolean; filteredWidgets?: any[] } = {}) => {
  const { userSetting } = useUserSettingStore();
  const { currentStoreId, iamUserProfile } = userProfileStore();
  const [config, setConfig] = useState() as any;
  const { isLoading, error, getWidgetData } = useDashboardData({
    config,
    enabled: enabled && !!config,
  });
  const [widgets, setWidgets] = useState(filteredWidgets) as any;

  useEffect(() => {
    if (currentStoreId) {
      setConfig(getDashboardConfig(currentStoreId));
    }
  }, [currentStoreId]);

  useEffect(() => {
    if (userSetting && iamUserProfile && currentStoreId) {
      const dashboard = userSetting.dashboard?.find(
        (dash: any) => dash.id === VIS_DASHBOARD.DASHBOARD_ID
      );
      const savedWidgets = dashboard?.widgets || [];
      const updatedWidgets = filteredWidgets.map((dwidget: any) => {
        const savedWidget = savedWidgets.find((w: any) => w.id === dwidget.id);
        return {
          ...dwidget,
          dashboardId: VIS_DASHBOARD.DASHBOARD_ID,
          fields: savedWidget?.fields,
          view: savedWidget?.view,
        };
      });
      setWidgets(updatedWidgets);
    }
  }, [filteredWidgets, userSetting, currentStoreId, iamUserProfile]);

  return {
    dashboardId: VIS_DASHBOARD.DASHBOARD_ID,
    widgets,
    isLoading: enabled ? (isLoading || !config) : false,
    error,
    getWidgetData,
  };
};
